import { UniDriver } from '@wix/unidriver-core';
import { baseUniDriverFactory } from '../utils/test-utils/unidriver';
import { infoIconDriverFactory } from '../InfoIcon/InfoIcon.uni.driver';
import { textUniDriverFactory } from '../Text/Text.uni.driver';
import { DATA_HOOKS } from './FormField.constants';
import { LabelSize } from './FormField.types';
import { StatusIndications } from '../common';

export const formFieldUniDriverFactory = (base: UniDriver, body: UniDriver) => {
  const charactersCounter = () =>
    base.$(`[data-hook*="${DATA_HOOKS.counter}"]`);

  return {
    ...baseUniDriverFactory(base),
    element: async (): Promise<HTMLElement> =>
      baseUniDriverFactory(base).element(),
    /** get children */
    getChildren: async (): Promise<HTMLElement | null> => {
      const baseUniDriverElement = base.$(
        `[data-hook*="${DATA_HOOKS.children}"]`,
      );
      return (await baseUniDriverElement.exists())
        ? baseUniDriverElement.getNative()
        : null;
    },
    /** get label */
    getLabel: async (): Promise<HTMLElement | null> => {
      const baseUniDriverElement = base.$(`[data-hook*="${DATA_HOOKS.label}"]`);
      return (await baseUniDriverElement.exists())
        ? baseUniDriverElement.getNative()
        : null;
    },
    /** returns label size */
    getLabelSize: async () => {
      const labelDriver = textUniDriverFactory(
        base.$(`[data-hook="${DATA_HOOKS.label}"]`),
      );
      return labelDriver.getSize() as Promise<LabelSize>;
    },
    /** returns true whether form field is required */
    isRequired: async () =>
      base.$(`[data-hook*="${DATA_HOOKS.asterisk}"]`).exists(),
    /** returns the length left */
    getLengthLeft: async () => {
      const counter = charactersCounter();
      return (await counter.exists())
        ? parseInt(await counter.prop('innerHTML'), 10)
        : null;
    },
    /** returns whether the form field length is exceeded */
    isLengthExceeded: async () => {
      const counter = charactersCounter();
      if (await counter.exists()) {
        const length = parseInt(await counter.prop('innerHTML'), 10);
        return length < 0;
      }
      return false;
    },
    /** returns true whether form field has tooltip */
    hasTooltip: async () => {
      return base.$(`[data-hook="${DATA_HOOKS.infoIcon}"]`).exists();
    },
    /** returns tooltip text of the info content */
    getInfoContent: async (): Promise<string> => {
      const testkit = infoIconDriverFactory(
        base.$(`[data-hook="${DATA_HOOKS.infoIcon}"]`),
        body,
      );
      return testkit.getContent();
    },
    /** get form field suffix */
    getSuffix: async (): Promise<HTMLElement | null> => {
      const suffixElement = base.$(`[data-hook="${DATA_HOOKS.suffix}"]`);

      return (await suffixElement.exists()) ? suffixElement.getNative() : null;
    },
    /** hovers over the formfield infoicon */
    hoverInfoIcon: async (): Promise<void> => {
      const testkit = infoIconDriverFactory(
        base.$(`[data-hook="${DATA_HOOKS.infoIcon}"]`),
        body,
      );
      return testkit.hover();
    },
    /** returns status message text */
    getStatusMessage: async () => {
      const testkit = textUniDriverFactory(
        base.$(`[data-hook="${DATA_HOOKS.statusMessage}"]`),
      );
      return testkit.getText();
    },
    /** return true if it has a given status */
    hasStatus: async (status: StatusIndications) =>
      (await base.attr('data-status')) === status,
  };
};

export type FormFieldUniDriver = ReturnType<typeof formFieldUniDriverFactory>;
